import _extends from "@babel/runtime/helpers/extends";
/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@emotion/react';
import { TableSharedCssClassName } from '@atlaskit/editor-common/styles';
import { akEditorStickyHeaderZIndex } from '@atlaskit/editor-shared-styles';
import { N40A } from '@atlaskit/theme/colors';
import { Table } from './table';
import { recursivelyInjectProps } from '../../utils/inject-props';
import { isTableResizingEnabled } from '../table';
export const tableStickyPadding = 8;
const modeSpecficStyles = {
  none: css({
    display: 'none'
  }),
  stick: css({
    position: 'fixed'
  }),
  'pin-bottom': css({
    position: 'absolute'
  })
};

// TODO: Quality ticket: https://product-fabric.atlassian.net/browse/DSP-4123
const fixedTableDivStaticStyles = (top, width, rendererAppearance) => {
  let stickyHeaderZIndex;
  if (isTableResizingEnabled(rendererAppearance)) {
    stickyHeaderZIndex = 13;
  } else {
    stickyHeaderZIndex = akEditorStickyHeaderZIndex;
  }
  return css(typeof top === 'number' && `top: ${top}px;`, {
    width: `${width}px`,
    zIndex: stickyHeaderZIndex,
    [`& .${TableSharedCssClassName.TABLE_CONTAINER}, & .${TableSharedCssClassName.TABLE_STICKY_WRAPPER} > table`]: {
      marginTop: 0,
      marginBottom: 0,
      tr: {
        background: "var(--ds-surface, white)"
      }
    },
    borderTop: `${tableStickyPadding}px solid ${"var(--ds-surface, white)"}`,
    background: "var(--ds-surface-overlay, white)",
    boxShadow: `0 6px 4px -4px ${`var(--ds-shadow-overflow-perimeter, ${N40A})`}`,
    "div[data-expanded='false'] &": {
      display: 'none'
    },
    [`& .${TableSharedCssClassName.TABLE_CONTAINER}.is-sticky.right-shadow::after, & .${TableSharedCssClassName.TABLE_CONTAINER}.is-sticky.left-shadow::before`]: {
      top: '0px',
      height: '100%'
    },
    "&.fixed-table-div-custom-table-resizing[mode='stick']": {
      zIndex: stickyHeaderZIndex
    }
  });
};
export const FixedTableDiv = props => {
  const {
    top,
    wrapperWidth,
    mode,
    rendererAppearance
  } = props;
  const fixedTableCss = [fixedTableDivStaticStyles(top, wrapperWidth, rendererAppearance), modeSpecficStyles === null || modeSpecficStyles === void 0 ? void 0 : modeSpecficStyles[mode]];
  const attrs = {
    mode
  };
  return jsx("div", _extends({}, attrs, {
    "data-testid": "sticky-table-fixed"
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    ,
    className: isTableResizingEnabled(rendererAppearance) ? 'fixed-table-div-custom-table-resizing' : '',
    css: fixedTableCss
  }), props.children);
};
export const StickyTable = ({
  top,
  left,
  mode,
  shadowClassNames,
  innerRef,
  wrapperWidth,
  tableWidth,
  isNumberColumnEnabled,
  layout,
  children,
  columnWidths,
  renderWidth,
  rowHeight,
  tableNode,
  rendererAppearance
}) => {
  let styles;
  /* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
  if (isTableResizingEnabled(rendererAppearance)) {
    styles = css({
      // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview
      top: mode === 'pin-bottom' ? top : undefined,
      position: 'absolute'
    });
  } else {
    styles = css({
      // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview
      left: left && left < 0 ? left : undefined,
      // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview
      top: mode === 'pin-bottom' ? top : undefined,
      position: 'relative'
    });
  }
  /* eslint-enable @atlaskit/design-system/ensure-design-token-usage */
  return jsx("div", {
    css: styles
  }, jsx(FixedTableDiv, {
    top: mode === 'stick' ? top : undefined,
    mode: rowHeight > 300 ? 'none' : mode,
    wrapperWidth: wrapperWidth,
    rendererAppearance: rendererAppearance
  }, jsx("div", {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    className: `${TableSharedCssClassName.TABLE_CONTAINER} is-sticky ${shadowClassNames || ''}`,
    "data-layout": layout,
    style: {
      width: tableWidth
    }
  }, jsx("div", {
    ref: innerRef
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    ,
    className: `${TableSharedCssClassName.TABLE_STICKY_WRAPPER}`,
    style: {
      // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
      overflow: 'hidden'
    }
  }, jsx(Table, {
    columnWidths: columnWidths,
    layout: layout,
    isNumberColumnEnabled: isNumberColumnEnabled,
    renderWidth: renderWidth,
    tableNode: tableNode,
    rendererAppearance: rendererAppearance
  },
  /**
   * @see https://product-fabric.atlassian.net/browse/ED-10235
   * We pass prop 'invisible' to our table's children nodes meaning
   * they exist inside of the 'invisible' duplicated table component that
   * enables sticky headers.
   */
  recursivelyInjectProps(children, {
    invisible: true
  }))))));
};

/**
 * Traverse DOM Tree upwards looking for table parents with "overflow: scroll".
 */
function findHorizontalOverflowScrollParent(table) {
  let parent = table;
  if (!parent) {
    return null;
  }
  while (parent = parent.parentElement) {
    // IE11 on Window 8 doesn't show styles from CSS when accessing through element.style property.
    const style = window.getComputedStyle(parent);
    if (style.overflow === 'scroll' || style.overflowY === 'scroll') {
      return parent;
    }
  }
  return null;
}
export class OverflowParent {
  constructor(ref) {
    this.ref = ref;
    this.ref = ref;
  }
  static fromElement(el) {
    return new OverflowParent(findHorizontalOverflowScrollParent(el) || window);
  }
  get isElement() {
    return this.ref instanceof HTMLElement;
  }
  get top() {
    if (this.ref instanceof HTMLElement) {
      return this.ref.getBoundingClientRect().top;
    }
    return 0;
  }
  addEventListener(type, cb, ...args) {
    this.ref.addEventListener(type, cb, ...args);
  }
  removeEventListener(type, cb, ...args) {
    this.ref.removeEventListener(type, cb, ...args);
  }
}